import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Navigation, Send, Camera, MapPin, CheckCircle2, Clock,
  MessageSquare, Package, RefreshCw, ChevronRight, CheckCheck,
} from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { ConversationPanel } from "@/components/admin/ConversationPanel";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

/* ==================== Admin/Driver: My Route ==============================
 * Driver-facing ordered stop list for a given day. Each stop offers:
 *   1. Next Stop     — deeplinks Google Maps turn-by-turn navigation.
 *   2. Send Notify   — computes live ETA (Distance Matrix), texts the customer
 *                      the exact "driver is on the way" copy + tracking link.
 *   3. Swipe to deliver/pickup — swipe a card right to mark it done + text customer.
 *   4. Scan & deliver — routes to the existing DriverScan flow for photo proof.
 */

/* ── SwipeCard ──────────────────────────────────────────────────────────────
 * Wraps a card to enable swipe-right-to-action on mobile.
 * Direct DOM manipulation during touchmove for smooth 60fps animation.
 * State is only updated on touchend for the "revealed" flag.
 */
const SwipeCard = ({ onAction, actionLabel = "Mark Done", actionIcon: Icon = CheckCheck, disabled = false, children }) => {
  const cardRef = useRef(null);
  const touch = useRef({ startX: 0, startY: 0, dragging: false, locked: false });
  const [revealed, setRevealed] = useState(false);
  const THRESHOLD = 88;
  const REVEAL_PX = 104;

  const handleTouchStart = (e) => {
    if (disabled) return;
    touch.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, dragging: false, locked: false };
  };

  const handleTouchMove = (e) => {
    if (disabled || touch.current.locked) return;
    const dx = e.touches[0].clientX - touch.current.startX;
    const dy = e.touches[0].clientY - touch.current.startY;
    if (!touch.current.dragging) {
      if (Math.abs(dx) > Math.abs(dy) + 4 && dx > 0) {
        touch.current.dragging = true;
      } else if (Math.abs(dy) > 8) {
        touch.current.locked = true; // vertical scroll — give up
        return;
      } else return;
    }
    e.preventDefault(); // prevent page scroll while swiping
    const offset = Math.min(Math.max(dx, 0), REVEAL_PX + 20);
    if (cardRef.current) cardRef.current.style.transform = `translateX(${offset}px)`;
  };

  const handleTouchEnd = () => {
    if (disabled) return;
    const dx = cardRef.current
      ? parseFloat(cardRef.current.style.transform?.replace("translateX(", "") || "0")
      : 0;
    if (cardRef.current) cardRef.current.style.transition = "transform 0.22s ease";
    if (dx >= THRESHOLD) {
      if (cardRef.current) cardRef.current.style.transform = `translateX(${REVEAL_PX}px)`;
      setRevealed(true);
    } else {
      if (cardRef.current) cardRef.current.style.transform = "translateX(0)";
      setRevealed(false);
    }
    touch.current = { startX: 0, startY: 0, dragging: false, locked: false };
  };

  const reset = () => {
    if (cardRef.current) {
      cardRef.current.style.transition = "transform 0.22s ease";
      cardRef.current.style.transform = "translateX(0)";
    }
    setRevealed(false);
  };

  const confirm = async () => {
    await onAction?.();
    reset();
  };

  return (
    <div className="relative mb-3 select-none" style={{ touchAction: "pan-y" }}>
      {/* Action strip — visible when card is swiped right */}
      <div
        className={`absolute inset-y-0 left-0 flex items-center rounded-2xl transition-opacity duration-200 ${revealed ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{ width: REVEAL_PX }}
      >
        <button
          onClick={confirm}
          className="w-full h-full flex flex-col items-center justify-center rounded-2xl bg-emerald-500/25 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/40 transition-colors gap-1"
          data-testid="swipe-action-btn"
        >
          <Icon className="w-6 h-6" />
          <span className="text-[10px] font-medium uppercase tracking-wide leading-none">{actionLabel}</span>
        </button>
      </div>

      {/* Main card — draggable */}
      <div
        ref={cardRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ transform: "translateX(0)", willChange: "transform" }}
      >
        {children}
      </div>

      {/* Swipe hint — only on unfinished delivery cards */}
      {!disabled && !revealed && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 pointer-events-none opacity-30">
          <span className="text-[9px] text-emerald-400 font-mono">›</span>
        </div>
      )}
    </div>
  );
};

const StopCard = ({ stop, onNotified, onRefresh, onContact, unread = 0 }) => {
  const [busy, setBusy] = useState(false);
  const [marking, setMarking] = useState(false);
  const [lastETA, setLastETA] = useState(stop.eta_min);
  const [showEtaInput, setShowEtaInput] = useState(false);
  const [customEtaMin, setCustomEtaMin] = useState("");
  const notified = !!stop.notified_at;
  const delivered = stop.kind === "delivery" ? !!stop.delivered_at : stop.status === "completed";

  const sendNotification = async (overrideMin) => {
    if (busy) return;
    setBusy(true);
    const getCoords = () =>
      new Promise((resolve) => {
        if (!("geolocation" in navigator)) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 },
        );
      });
    try {
      const coords = overrideMin ? null : await getCoords();
      const body = { kind: stop.kind, driver_lat: coords?.lat, driver_lng: coords?.lng };
      if (overrideMin) body.eta_override_min = parseInt(overrideMin, 10);
      const { data } = await axios.post(
        `${API}/driver/reservations/${stop.reservation_id}/enroute`,
        body,
      );
      setLastETA(data.eta_min);
      const sourceHint = data.eta_source === "gps" ? "GPS" : data.eta_source === "depot" ? "depot" : data.eta_source === "manual" ? "manual" : "estimate";
      toast.success(
        data.sms_sent
          ? `Customer notified · ETA ${data.eta_text || data.eta_min + " min"} (${sourceHint})`
          : `ETA ${data.eta_text || data.eta_min + " min"} · SMS deferred (${sourceHint})`
      );
      setShowEtaInput(false);
      setCustomEtaMin("");
      onNotified?.(stop, data);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Send notification failed");
    } finally {
      setBusy(false);
    }
  };

  const quickAction = async () => {
    if (marking || delivered) return;
    setMarking(true);
    const endpoint = stop.kind === "delivery" ? "quick-deliver" : "quick-pickup";
    try {
      const { data } = await axios.post(`${API}/driver/reservations/${stop.reservation_id}/${endpoint}`);
      const msg = stop.kind === "delivery"
        ? `Delivered${data.sms_sent ? " · Customer notified ✓" : ""}`
        : `Picked up${data.sms_sent ? " · Customer notified ✓" : ""}`;
      toast.success(msg);
      onRefresh?.();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Action failed");
    } finally {
      setMarking(false);
    }
  };

  const kindChip = stop.kind === "pickup"
    ? "bg-orange-500/15 border-orange-500/40 text-orange-200"
    : "bg-emerald-500/15 border-emerald-500/40 text-emerald-200";

  const actionLabel = stop.kind === "delivery" ? "Delivered" : "Picked Up";

  const cardInner = (
    <Card className={`p-4 transition-colors duration-300 ${delivered ? "border-emerald-500/40 bg-emerald-500/5" : ""}`} data-testid={`stop-card-${stop.reservation_id}-${stop.kind}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/40 text-[#FFD166] flex items-center justify-center font-serif text-lg flex-shrink-0" data-testid={`stop-seq-${stop.sequence || "?"}`}>
          {stop.sequence || "•"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-medium uppercase tracking-widest px-2 py-0.5 rounded-full border ${kindChip}`}>{stop.kind}</span>
            <span className="text-[11px] text-[#B9C8D5]">{stop.slot_label || stop.slot}</span>
            {delivered && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300 uppercase"><CheckCheck className="w-3 h-3" /> {actionLabel}</span>}
            {notified && !delivered && <span className="text-[10px] text-[#FFD166] uppercase">📣 Notified</span>}
          </div>
          <div className="font-serif text-xl text-[#F6F1E4] mt-1 truncate">{stop.customer_name}</div>
          <div className="text-sm text-[#B9C8D5] flex items-center gap-1 mt-0.5 truncate"><MapPin className="w-3.5 h-3.5 flex-shrink-0" /> {stop.address}</div>
          <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
            <button
              type="button"
              onClick={() => onContact?.(stop)}
              className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#FFD166]/50 bg-[#FFD166]/10 text-[#FFD166] hover:bg-[#FFD166]/20 transition-colors font-medium"
              data-testid={`stop-contact-${stop.reservation_id}`}
            >
              <MessageSquare className="w-3.5 h-3.5" /> Contact Customer
              {unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center" data-testid={`stop-unread-${stop.reservation_id}`}>
                  {unread}
                </span>
              )}
            </button>
            {!stop.sms_allowed && (
              <span className="text-[10px] text-[#F4A93A]" data-testid={`stop-email-only-${stop.reservation_id}`}>Email only — no SMS consent</span>
            )}
          </div>
        </div>
      </div>

      {(stop.line_items || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#FFD166]/10">
          <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] mb-1"><Package className="w-3 h-3 inline mr-1" /> Gear ({stop.line_items.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {stop.line_items.map((li, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-black/30 border border-[#FFD166]/20 text-[#F6F1E4]">
                × {li.quantity} — {li.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {stop.notes && (
        <div className="mt-2 text-[11px] text-[#B9C8D5] italic">Notes: {stop.notes}</div>
      )}

      {(notified || lastETA) && (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-[#FFD166]">
          <Clock className="w-3.5 h-3.5" />
          Last ETA sent: <b>{lastETA || stop.eta_min} min</b>
          {stop.notified_at && <span className="text-[#B9C8D5]">· {new Date(stop.notified_at).toLocaleTimeString()}</span>}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <a
          href={stop.directions_url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary flex items-center justify-center gap-2"
          data-testid={`stop-next-stop-${stop.reservation_id}`}
        >
          <Navigation className="w-4 h-4" /> Next Stop
        </a>
        <div className="flex flex-col gap-1">
          <Btn
            onClick={() => sendNotification(customEtaMin || undefined)}
            disabled={busy || delivered}
            className="w-full"
            variant="ghost"
            data-testid={`stop-send-notify-${stop.reservation_id}`}
          >
            <Send className="w-4 h-4" /> {busy ? "Sending…" : notified ? "Resend" : "Send Notification"}
          </Btn>
          {!delivered && (
            <button
              className="text-[10px] text-[#B9C8D5] hover:text-[#FFD166] text-center"
              onClick={() => setShowEtaInput((v) => !v)}
              data-testid={`stop-eta-toggle-${stop.reservation_id}`}
            >
              {showEtaInput ? "▲ hide manual ETA" : "▼ set custom ETA"}
            </button>
          )}
          {showEtaInput && (
            <div className="flex items-center gap-1 mt-0.5">
              <input
                type="number"
                min="1"
                max="180"
                placeholder="min"
                value={customEtaMin}
                onChange={(e) => setCustomEtaMin(e.target.value)}
                className="w-16 bg-black/40 border border-[#FFD166]/30 text-[#F6F1E4] px-2 py-1 rounded text-xs"
                data-testid={`stop-eta-input-${stop.reservation_id}`}
              />
              <span className="text-[10px] text-[#B9C8D5]">minutes (overrides GPS)</span>
            </div>
          )}
        </div>
        {stop.kind === "delivery" ? (
          delivered ? (
            <div className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm border border-emerald-500/40 text-emerald-300 bg-emerald-500/5" data-testid={`stop-delivered-badge-${stop.reservation_id}`}>
              <CheckCheck className="w-4 h-4" /> Delivered
            </div>
          ) : (
            <Link
              to={`/admin/scan?mode=delivery&code=EGR-${(stop.short_code || "").toUpperCase()}`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm border border-[#FFD166]/40 text-[#FFD166] hover:bg-[#FFD166]/10 transition-colors"
              data-testid={`stop-scan-${stop.reservation_id}`}
            >
              <Camera className="w-4 h-4" /> Photo & Deliver
            </Link>
          )
        ) : (
          delivered ? (
            <div className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm border border-emerald-500/40 text-emerald-300 bg-emerald-500/5" data-testid={`stop-delivered-badge-${stop.reservation_id}`}>
              <CheckCheck className="w-4 h-4" /> Picked Up
            </div>
          ) : (
            <Link
              to={`/admin/scan?mode=pickup&code=EGR-${(stop.short_code || "").toUpperCase()}`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm border border-[#FFD166]/40 text-[#FFD166] hover:bg-[#FFD166]/10 transition-colors"
              data-testid={`stop-scan-${stop.reservation_id}`}
            >
              <Camera className="w-4 h-4" /> Scan Pickup
            </Link>
          )
        )}
      </div>

      {stop.tracking_token && (
        <div className="mt-2 text-[10px] text-[#B9C8D5] break-all">
          Tracking: <a href={`/track/${stop.tracking_token}`} target="_blank" rel="noopener noreferrer" className="text-[#FFD166] hover:underline">/track/{stop.tracking_token}</a>
        </div>
      )}

      {/* Swipe hint text — only on undelivered stops */}
      {!delivered && (
        <div className="mt-3 pt-2 border-t border-[#FFD166]/10 text-[10px] text-[#B9C8D5]/50 flex items-center gap-1" data-testid={`stop-swipe-hint-${stop.reservation_id}`}>
          <span className="text-emerald-500/60">›</span> Swipe right to mark {stop.kind === "delivery" ? "delivered" : "picked up"} &amp; text customer
        </div>
      )}
    </Card>
  );

  return (
    <SwipeCard
      onAction={quickAction}
      actionLabel={marking ? "…" : actionLabel}
      actionIcon={marking ? RefreshCw : CheckCheck}
      disabled={delivered || marking}
    >
      {cardInner}
    </SwipeCard>
  );
};


export const MyRoute = () => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [day, setDay] = useState(today);
  const [data, setData] = useState({ stops: [], driver: null });
  const [loading, setLoading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState({ enabled: false, lastAt: null, error: null });
  const [contactStop, setContactStop] = useState(null);
  const [unread, setUnread] = useState({});

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await axios.get(`${API}/comms/my-unread`);
        setUnread(data.by_reservation || {});
      } catch (_) { /* silent */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30_000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/driver/my-stops`, { params: { day } });
      setData(data);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Couldn't load your route");
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  // ------------------------------------------------------------------
  // Live GPS broadcaster — while ANY stop is en-route (tracking_active),
  // watch geolocation and POST the driver's coords to the backend every
  // ~30 s so the customer's /track/:token page can show a live dot.
  // Stops automatically when all en-route stops are delivered or the
  // driver navigates away.
  // ------------------------------------------------------------------
  const activeStops = useMemo(
    () => (data.stops || []).filter((s) => s.tracking_active),
    [data.stops]
  );
  const activeStopsKey = activeStops.map((s) => `${s.reservation_id}:${s.kind}`).join(",");

  useEffect(() => {
    if (!activeStops.length) {
      setGpsStatus((s) => ({ ...s, enabled: false }));
      return;
    }
    if (!("geolocation" in navigator)) {
      setGpsStatus({ enabled: false, lastAt: null, error: "This browser can't share location." });
      return;
    }

    let lastPost = 0;
    let cancelled = false;

    const post = async (coords) => {
      // Send in parallel to every currently-active stop. Backend guards on
      // driver_id, so a stop belonging to another driver is ignored safely.
      const now = Date.now();
      if (now - lastPost < 25_000) return; // hard-throttle at 25 s
      lastPost = now;
      await Promise.all(activeStops.map((s) =>
        axios.post(`${API}/driver/reservations/${s.reservation_id}/location`, {
          kind: s.kind,
          lat: coords.latitude,
          lng: coords.longitude,
          heading: Number.isFinite(coords.heading) ? coords.heading : null,
          speed_mph: Number.isFinite(coords.speed) ? coords.speed * 2.23694 : null,
        }).catch(() => null)
      ));
      if (!cancelled) setGpsStatus({ enabled: true, lastAt: new Date().toISOString(), error: null });
    };

    const watchId = navigator.geolocation.watchPosition(
      (pos) => post(pos.coords),
      (err) => setGpsStatus({ enabled: false, lastAt: null, error: err.message || "GPS unavailable" }),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 15000 }
    );

    // Fallback: force a getCurrentPosition every 30 s so we still get a
    // ping when watchPosition is stingy about firing (some Android WebViews).
    const interval = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => post(pos.coords),
        () => {},
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 15000 }
      );
    }, 30_000);

    setGpsStatus({ enabled: true, lastAt: null, error: null });
    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(watchId);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStopsKey]);

  const deliveries = data.stops?.filter((s) => s.kind === "delivery") || [];
  const pickups = data.stops?.filter((s) => s.kind === "pickup") || [];

  return (
    <div className="space-y-5" data-testid="my-route-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Navigation className="w-6 h-6 text-[#FFD166] flex-shrink-0" />
        <h1 className="font-serif text-3xl">My Route</h1>
        {data.driver?.name && <span className="text-xs text-[#B9C8D5]">· {data.driver.name}</span>}
        {activeStops.length > 0 && (
          <span
            className={`ml-auto sm:ml-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-widest ${
              gpsStatus.enabled && gpsStatus.lastAt
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : "border-amber-500/40 bg-amber-500/10 text-amber-200"
            }`}
            title={gpsStatus.error || (gpsStatus.lastAt ? `Last ping ${new Date(gpsStatus.lastAt).toLocaleTimeString()}` : "Waiting for GPS…")}
            data-testid="my-route-gps-status"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${gpsStatus.enabled && gpsStatus.lastAt ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} />
            {gpsStatus.enabled && gpsStatus.lastAt ? "Live GPS on" : gpsStatus.error ? "GPS off" : "Requesting GPS…"}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm"
          data-testid="my-route-date"
        />
        <Btn onClick={load} variant="ghost" data-testid="my-route-reload">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reload
        </Btn>
        <div className="text-xs text-[#B9C8D5] ml-auto">
          {deliveries.length} {deliveries.length === 1 ? "delivery" : "deliveries"} · {pickups.length} {pickups.length === 1 ? "pickup" : "pickups"}
        </div>
      </div>

      {loading && <Card className="p-6 text-center text-[#B9C8D5]">Loading your stops…</Card>}

      {!loading && data.stops?.length === 0 && (
        <Card className="p-8 text-center text-[#B9C8D5]" data-testid="my-route-empty">
          <ChevronRight className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No stops assigned to you for this date. Ask an admin to build your route from the Route Builder tab.
        </Card>
      )}

      {deliveries.length > 0 && (
        <section data-testid="my-route-deliveries">
          <div className="eyebrow mb-2">Deliveries</div>
          {deliveries.map((s) => (
            <StopCard key={`d-${s.reservation_id}`} stop={s} onRefresh={load} onContact={setContactStop} unread={unread[s.reservation_id] ?? s.unread_messages ?? 0} />
          ))}
        </section>
      )}

      {pickups.length > 0 && (
        <section data-testid="my-route-pickups">
          <div className="eyebrow mb-2 mt-6">Pickups</div>
          {pickups.map((s) => (
            <StopCard key={`p-${s.reservation_id}`} stop={s} onRefresh={load} onContact={setContactStop} unread={unread[s.reservation_id] ?? s.unread_messages ?? 0} />
          ))}
        </section>
      )}

      {contactStop && (
        <ConversationPanel
          reservationId={contactStop.reservation_id}
          customerName={contactStop.customer_name}
          onClose={() => setContactStop(null)}
        />
      )}
    </div>
  );
};

export default MyRoute;
